import { type State } from './state.js';
import {
  getEnabledPayouts,
  isCountrySanctioned,
  isCountrySupported,
} from './utils.js';

const handleGoForwardAfterSelectingCountry = (state: State): State => {
  if (isCountrySanctioned(state)) {
    return {
      ...state,
      view: 'VIEW_Sanctioned_Country',
    };
  }

  if (!isCountrySupported(state)) {
    return {
      ...state,
      view: 'VIEW_Country_Not_Supported',
    };
  }

  return {
    ...state,
    payoutTypesEnabled: getEnabledPayouts(state),
    view: 'VIEW_Select_Payout_Method',
  };
};

// This is being disabled because the exhaustive check rule renders this check unnecessary
// If this rule is not disabled, the last return statement will be 'unreachable code'
// eslint-disable-next-line consistent-return
const handleGoForwardAfterSelectingPayoutMethod = (state: State): State => {
  switch (state.selectedPayoutType) {
    case null: {
      return state;
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Airwallex_Local_Bank':

    case 'PAYOUT_Airwallex_US_Bank_Only': {
      if (
        state.payoutAccountEligibility.airwallexPayouts.prerequisite ===
        'PERSONA_VERIFICATION'
      ) {
        return {
          ...state,
          view: 'VIEW_Persona_Note',
        };
      }

      return {
        ...state,
        view: 'VIEW_Airwallex_Beneficiary_Form',
      };
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Crypto': {
      switch (state.payoutAccountEligibility.usdcPayouts.prerequisite) {
        case null:
        case undefined: {
          return {
            ...state,
            view: 'VIEW_Crypto_Education',
          };
        }

        case 'PERSONA_VERIFICATION': {
          return {
            ...state,
            view: 'VIEW_Persona_Note',
          };
        }
      }
    }

    case 'PAYOUT_Payoneer': {
      throw new Error('This code path should not be reachable');
    }

    case 'PAYOUT_Paypal': {
      return {
        ...state,
        view: 'VIEW_Paypal_Connect_Form',
      };
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Stripe_Local_Bank': {
      switch (
        state.payoutAccountEligibility.stripeBankAccountPayouts.flow
          ?.prerequisite
      ) {
        case 'AGREE_TO_RECIPIENT_AGREEMENT': {
          return {
            ...state,
            view: 'VIEW_Stripe_Note',
          };
        }

        case null:

        case undefined: {
          if (state.initialCountryCode === 'US') {
            // For US users with a Stripe account, they can directly add their
            // bank account details on Contra. Our platform accommodates the
            // necessary US bank form requirements, eliminating the need to
            // redirect to Stripe's onboarding flow.
            return {
              ...state,
              view: 'VIEW_Bank_Account_Form',
            };
          }

          return {
            ...state,
            view: 'VIEW_Stripe_Note',
          };
        }

        case 'PERSONA_VERIFICATION': {
          return {
            ...state,
            view: 'VIEW_Persona_Note',
          };
        }
      }

      // this is dead code because of the exhaustive switch above
      // adding this to make eslint happy for the fallthrough rule
      throw new Error('This code path should not be reachable');
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Stripe_Local_Debit_Card': {
      switch (
        state.payoutAccountEligibility.stripeDebitCardPayouts.prerequisite
      ) {
        case 'AGREE_TO_RECIPIENT_AGREEMENT':
        case 'BANK_ACCOUNT': {
          return {
            ...state,
            view: 'VIEW_Stripe_Note',
          };
        }

        case null:
        case undefined: {
          return {
            ...state,
            view: 'VIEW_Debit_Card_Form',
          };
        }

        case 'PERSONA_VERIFICATION': {
          return {
            ...state,
            view: 'VIEW_Persona_Note',
          };
        }
      }

      // this is dead code because of the exhaustive switch above
      // adding this to make eslint happy for the fallthrough rule
      throw new Error('This code path should not be reachable');
    }

    // ========== PAYOUT TYPE CASE ENDS ==========
  }
};

// This is being disabled because the exhaustive check rule renders this check unnecessary
// If this rule is not disabled, the last return statement will be 'unreachable code'
// eslint-disable-next-line consistent-return
const handleGoForwardAfterPersonaVerification = (state: State): State => {
  switch (state.selectedPayoutType) {
    case null: {
      return state;
    }

    // ========== PAYOUT TYPE ENDS ==========
    case 'PAYOUT_Airwallex_Local_Bank':
    case 'PAYOUT_Airwallex_US_Bank_Only': {
      if (!state.payoutAccountEligibility.airwallexPayouts.prerequisite) {
        return {
          ...state,
          view: 'VIEW_Airwallex_Beneficiary_Form',
        };
      }

      return {
        ...state,
        view: 'VIEW_Persona_Failure',
      };
    }

    case 'PAYOUT_Crypto': {
      if (
        state.payoutAccountEligibility.usdcPayouts.eligibilityStatus ===
          'ELIGIBLE' &&
        state.payoutAccountEligibility.usdcPayouts.prerequisite === null
      ) {
        return {
          ...state,
          view: 'VIEW_Crypto_Education',
        };
      }

      return {
        ...state,
        view: 'VIEW_Persona_Failure',
      };
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Payoneer': {
      throw new Error('This code path should not be reachable');
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Paypal': {
      return {
        ...state,
      };
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Stripe_Local_Bank': {
      if (
        state.payoutAccountEligibility.stripeBankAccountPayouts
          .eligibilityStatus === 'ELIGIBLE' &&
        state.payoutAccountEligibility.stripeBankAccountPayouts.flow
          ?.__typename ===
          'StripeBankAccountPayoutAccountEligibilityFlowContraInApp' &&
        !state.payoutAccountEligibility.stripeBankAccountPayouts.flow
          ?.prerequisite
      ) {
        return {
          ...state,
          view: 'VIEW_Bank_Account_Form',
        };
      }

      return {
        ...state,
        view: 'VIEW_Persona_Failure',
      };
    }

    // ========== PAYOUT TYPE CASE ENDS ==========

    case 'PAYOUT_Stripe_Local_Debit_Card': {
      if (
        state.payoutAccountEligibility.stripeDebitCardPayouts
          .eligibilityStatus === 'ELIGIBLE' &&
        state.payoutAccountEligibility.stripeDebitCardPayouts.prerequisite ===
          null
      ) {
        return {
          ...state,
          view: 'VIEW_Debit_Card_Form',
        };
      }

      return {
        ...state,
        view: 'VIEW_Persona_Failure',
      };
    }

    // ========== PAYOUT TYPE CASE ENDS ==========
  }
};

const handleGoForwardAfterRedirectFromStripe = (state: State): State => {
  if (state.stripeRequiresOnboardingAction) {
    return {
      ...state,
      view: 'VIEW_Stripe_More_Information',
    };
  }

  if (
    state.selectedPayoutType === 'PAYOUT_Stripe_Local_Bank' &&
    state.payoutAccountEligibility.stripeBankAccountPayouts
      .eligibilityStatus === 'ELIGIBLE' &&
    !state.payoutAccountEligibility.stripeBankAccountPayouts.flow?.prerequisite
  ) {
    return {
      ...state,
      view: 'VIEW_Payout_Setup_Successful',
    };
  }

  if (
    state.selectedPayoutType === 'PAYOUT_Stripe_Local_Debit_Card' &&
    state.payoutAccountEligibility.stripeDebitCardPayouts.eligibilityStatus ===
      'ELIGIBLE' &&
    !state.payoutAccountEligibility.stripeDebitCardPayouts.prerequisite
  ) {
    return {
      ...state,
      view: 'VIEW_Debit_Card_Form',
    };
  }

  return {
    ...state,
    view: 'VIEW_Stripe_More_Information',
  };
};

// This is being disabled because the exhaustive check rule renders this check unnecessary
// If this rule is not disabled, the last return statement will be 'unreachable code'
// eslint-disable-next-line consistent-return
export const handleGoForward = (state: State): State => {
  switch (state.view) {
    case null:

    case 'VIEW_Country_Not_Supported':

    case 'VIEW_Payoneer_Return':
    case 'VIEW_Payout_Setup_Successful':

    case 'VIEW_Paypal_Connect_Form':

    case 'VIEW_Paypal_Return':

    case 'VIEW_Persona_Failure':

    case 'VIEW_Sanctioned_Country': {
      return {
        ...state,
        view: null,
      };
    }

    case 'VIEW_Airwallex_Beneficiary_Form':

    case 'VIEW_Bank_Account_Form':

    case 'VIEW_Crypto_Form':
    case 'VIEW_Debit_Card_Form': {
      return {
        ...state,
        view: 'VIEW_Payout_Setup_Successful',
      };
    }

    case 'VIEW_Crypto_Disclaimer': {
      return {
        ...state,
        view: 'VIEW_Crypto_Form',
      };
    }

    case 'VIEW_Crypto_Education': {
      return {
        ...state,
        view: 'VIEW_Crypto_Disclaimer',
      };
    }

    case 'VIEW_Persona': {
      if (
        state.verifiedIdentity &&
        state.verifiedIdentity.location &&
        !state.verifiedIdentity.locationMatchesUserAccountLocation
      ) {
        return {
          ...state,
          view: 'VIEW_Persona_Update_Location',
        };
      }

      return handleGoForwardAfterPersonaVerification(state);
    }

    case 'VIEW_Persona_Note': {
      return {
        ...state,
        view: 'VIEW_Persona',
      };
    }

    case 'VIEW_Persona_Update_Location': {
      return handleGoForwardAfterPersonaVerification(state);
    }

    case 'VIEW_Select_Country': {
      return handleGoForwardAfterSelectingCountry(state);
    }

    case 'VIEW_Select_Payout_Method': {
      return handleGoForwardAfterSelectingPayoutMethod(state);
    }

    case 'VIEW_Stripe_More_Information':
    case 'VIEW_Stripe_Note': {
      // In these cases when the next step is to redirect to Stripe,
      // it's the caller's responsibility to do that.
      // Given that the app state will be destroyed after that,
      // there's no reason to handle this in the reducer by
      // setting a callback as part of the reducer state

      return state;
    }

    case 'VIEW_Stripe_Return': {
      return handleGoForwardAfterRedirectFromStripe(state);
    }
  }
};
